/* global I18n */

import React from "react"
import _ from "lodash"
import {createRoot} from "react-dom/client"
import {Controller} from "@hotwired/stimulus"
import {SmallTickingClock} from "../components/ticking_clock"
import {trial_time_left, can_show_ticking_clock} from "../user_trial_time"
import destroy_fullscreen_layer from "../destroy_fullscreen_layer"
import init_trial_banner from "../init/trial_banner"
import viewport_helper from "../viewport_helper"
import lazy_loader from "../lazy_loader"
import signals from "../signals"
import timeouts from "../timeouts"
import intervals from "../intervals"
import url_helper from "../url_helper"
import modal from "../modal"
import $ from "cash-dom"
import user_settings from "../user_settings"

class ApplicationController extends Controller {
  static targets = [
    "trialClockSmall", "changelog", "startTrialCountdown", "trustItem"
  ]

  initialize() {
    this.current_trust_index = 0
    this.trust_slider_interval = null
  }

  connect() {
    // we need this when changing the locale with a turbo link
    window.I18n.locale = window.gon.locale
    modal(".modal[data-show]")
    this.init_trial_clocks()
    for (const countdown of this.startTrialCountdownTargets) {
      init_trial_banner(countdown)
    }
    this.move_viewport_to_element()
    // waits for the promises of the lazy loads happening on page load
    lazy_loader.init().then(() => this.move_viewport_to_element())
    this.save_login_data_to_local_storage()
    this.init_trust_slider()
  }

  save_login_data_to_local_storage() {
    if (!window.gon.current_user) return

    const is_email_same =
      user_settings.get("email") === window.gon.current_user.email
    const is_login_method_same = _.isEqual(
      user_settings.get("login_methods"),
      window.gon.current_user.authentication_methods
    )
    if (is_email_same && is_login_method_same) return

    user_settings.register({
      email: window.gon.current_user.email,
      login_methods: window.gon.current_user.authentication_methods
    })
  }

  disconnect() {
    signals.remove_all()
    intervals.remove_all()
    timeouts.remove_all()
    $(document).off()
    $(window).off()
    destroy_fullscreen_layer()
    // removes gon script in head because turbo
    // always adds a new script tag when gon changes
    $("script[data-gon-cleanup]").remove()
    // clean up open modals
    $(".react-modal-portal").remove()
    // https://github.com/MiniProfiler/rack-mini-profiler
    // #using-in-spa-applications
    window.MiniProfiler?.pageTransition()
    window.ActionCable?.subscriptions?.remove_all()
    clearInterval(this.trust_slider_interval)
  }

  on_setup_complete_modal_hide() {
    setTimeout(() => {
      // using timeout here to wait until modal closing animation is over
      this.dispatch("setup_complete_modal_hidden")
    }, 1000)
  }

  move_viewport_to_element() {
    const $element = url_helper.get_hash_as_element()
    if (!$element?.length) return
    // needs to be called in a timeout, so it gets called after the browser
    // moves the viewport automatically (happens on Firefox)
    timeouts.set(() => viewport_helper.go_to($element), 10)
  }

  init_trial_clocks() {
    if (!can_show_ticking_clock()) return
    // not running where we have a big clock already
    if (this.hasTrialClockBigTarget) return
    if (!this.hasTrialClockSmallTarget) return

    for (const changelog of this.changelogTargets) {
      changelog.style.display = "none"
    }
    for (const trial_clock of this.trialClockSmallTargets) {
      createRoot(trial_clock).render(
        <SmallTickingClock
          pricing_path={window.gon.pricing_path}
          time_left={trial_time_left()}
        />
      )
    }
  }

  throw_confetti() {
    import(/* webpackChunkName: "party_js" */ "party-js")
      .then(({confetti}) => confetti(document.body, {
        count: 300,
        spread: 10,
        speed: 1000
      }))
  }

  disable_button(event) {
    const button = event.currentTarget.querySelector("button")
    button.style.cursor = "progress"
    button.disabled = true
    const id = event.target.dataset.loadingContent
    if (id) document.getElementById(id).classList.add("loading")
  }

  loading_button(event) {
    const button = event.currentTarget.querySelector("button")
    button.disabled = true
    button.querySelector("svg > use").remove()
    button.classList.add("spinner")
  }

  disable_link(event) {
    const link = event.currentTarget
    link.style.cursor = "progress"
    const id = link.dataset.loadingContent
    if (id) document.getElementById(id).classList.add("loading")
  }

  download_study_guide(event) {
    const link = event.currentTarget
    if (link.getAttribute("href") === window.gon.dashboard_path) return true

    // the timeout needs to be over 500 because tracking
    // defers the link execution as well
    setTimeout(() => {
      link.setAttribute("href", window.gon.dashboard_path)
      link.innerHTML = I18n.t("js.study_guide_ebook.go_to_dashboard")
      link.removeAttribute("target")
    }, 700)
  }

  open_start_trial_modal() {
    modal("#start_trial_modal")
  }

  adjust_navigation_popover(event) {
    const popover = event.currentTarget.querySelector(".popover")
    if (!popover) return

    // 10 pixel difference because we want to show the submenu indended
    popover.style.left = `${event.currentTarget.offsetWidth - 10}px`
    const rect = popover.getBoundingClientRect()
    // if popover isn't completely visible in window
    if (rect.x + rect.width > window.innerWidth) {
      // show it on the left side
      popover.style.left = `${-rect.width + 10}px`
    }
  }

  init_trust_slider() {
    const items = this.trustItemTargets
    if (!items.length) return

    items.forEach(el => el.classList.remove("is-visible"))
    items[this.current_trust_index].classList.add("is-visible")
    this.trust_slider_interval = setInterval(() => {
      items[this.current_trust_index].classList.remove("is-visible")
      this.current_trust_index = (this.current_trust_index + 1) % items.length
      items[this.current_trust_index].classList.add("is-visible")
    }, 5000)
  }
}

export default ApplicationController
